/**
 * Branding store — FRONT-CUTOVER-BRANDING (orden 1d4c8dad, B2.3 follow-up).
 *
 * MODELO NUEVO: el branding visual de una app vive en su DISEÑO —
 * AARPIA.clientes.interfaz.publicada (estado `publicada`) — y el contenedor es
 * AARPIA.clientes.app.operativa. Se resuelve POR SLUG (derivado del hostname,
 * §lib/config.slugApp), nunca por hit-policy ambigua sobre una lista: ese era
 * el bug "Demo Fábrica" (el store viejo tomaba la primera app.configurada viva
 * del CO, que era otra app).
 *
 * Lectura:  app.operativa (slug) → identidad/subdomain · interfaz.publicada
 *           (app_slug + publicada) → branding visual.
 * Escritura: EDITAR → PUBLICAR sobre la interfaz (cada edición = versión nueva
 *           gobernada §13-en-loop). El subdomain es identidad del contenedor,
 *           no branding — aquí es solo lectura.
 *
 * Mantiene `activo`/`activoAppId` (los consumen BrandingProvider, Sidebar,
 * ConexionesApp). `activoAppId` pasa a ser el id de la instancia de
 * app.operativa.
 */
import { create } from 'zustand'
import { api } from '@/lib/api'
import { useAuthStore } from '@/stores/auth'
import { vsn } from '@/lib/version'
import { slugApp } from '@/lib/config'

export interface BrandingConfig {
  logo_url?: string
  nombre_comercial?: string
  color_primario?: string // hex
  color_acento?: string // hex
  subdomain?: string
  favicon_url?: string
}

interface BrandingState {
  /** Branding aplicado actualmente (de la interfaz publicada de esta app). */
  activo: BrandingConfig
  /** id de la instancia de app.operativa de esta app (null si no existe). */
  activoAppId: string | null
  /** estado de la app.operativa (registrada | configurada | operativa | ...). */
  activoAppEstado: string | null
  /** id de la interfaz.publicada vinculada (null si la app no tiene diseño publicado). */
  interfazId: string | null
  cargando: boolean

  /** Resuelve app + interfaz por slug (RLS del CO activo) y aplica el branding. */
  applyForCo: (coId: string | null) => Promise<void>
  /** Persiste el branding vía EDITAR→PUBLICAR sobre la interfaz. true si OK. */
  guardar: (b: BrandingConfig) => Promise<boolean>
  reset: () => void
}

const BRANDING_DEFAULT: BrandingConfig = { nombre_comercial: 'Arquitecto' }
const rutaApp = () => `clientes/app/operativa/v${vsn('clientes', 'app', 'operativa', '1.0')}`
const rutaInterfaz = () => `clientes/interfaz/publicada/v${vsn('clientes', 'interfaz', 'publicada', '1.0')}`

function brandingDe(interfazPayload: any, appPayload: any): BrandingConfig {
  return {
    logo_url: interfazPayload?.logo_url || undefined,
    nombre_comercial: interfazPayload?.nombre_comercial || appPayload?.nombre || undefined,
    color_primario: interfazPayload?.color_primario || undefined,
    color_acento: interfazPayload?.color_acento || undefined,
    subdomain: appPayload?.subdomain || undefined,
    favicon_url: interfazPayload?.favicon_url || undefined,
  }
}

export const useBrandingStore = create<BrandingState>()((set, get) => ({
  activo: BRANDING_DEFAULT,
  activoAppId: null,
  activoAppEstado: null,
  interfazId: null,
  cargando: false,

  async applyForCo(coId) {
    const token = useAuthStore.getState().token
    if (!coId || !token) {
      set({ activo: BRANDING_DEFAULT, activoAppId: null, activoAppEstado: null, interfazId: null })
      return
    }
    set({ cargando: true })
    const slug = slugApp()
    const host = typeof window !== 'undefined' ? window.location.hostname : ''
    try {
      const [rApps, rInts] = await Promise.all([
        api.get<any>(`/canonico/${rutaApp()}/instancias?limit=100`, token),
        api.get<any>(`/canonico/${rutaInterfaz()}/instancias?limit=100`, token),
      ])
      const norm = (r: any) => (r?.data || []).map((it: any) => ({
        id: it.id,
        current_state: it.attributes?.current_state ?? it.current_state,
        payload: it.attributes?.payload ?? it.payload,
      }))
      // CDD-2: resolución por IDENTIDAD DE SERVING vía `dns` (el FQDN donde se sirve la
      // app) — la MISMA fuente que usan el routing del back (root.ts deriveAppIdFromHost:
      // payload.dns === host) y el CORS derivado. Una sola verdad de "dónde sirve la app".
      // Fallbacks (subdomain, slug) solo por compatibilidad hasta que el back deprecación
      // subdomain. chat.asesorbi.es (dns) → app asesorbi-chat → interfaz AsesorBI.
      const apps = norm(rApps).filter((a: any) => !['deprecada', 'eliminada', 'error'].includes(a.current_state))
      const app =
        apps.find((a: any) => a.payload?.dns === host) ||
        apps.find((a: any) => a.payload?.subdomain === host) ||
        apps.find((a: any) => a.payload?.slug === slug)
      const slugApp_ = app?.payload?.slug || slug
      const interfaz = norm(rInts).find(
        (i: any) => i.payload?.app_slug === slugApp_ && i.current_state === 'publicada',
      )
      if (app || interfaz) {
        set({
          activo: brandingDe(interfaz?.payload, app?.payload),
          activoAppId: app?.id ?? null,
          activoAppEstado: app?.current_state ?? null,
          interfazId: interfaz?.id ?? null,
        })
      } else {
        set({ activo: BRANDING_DEFAULT, activoAppId: null, activoAppEstado: null, interfazId: null })
      }
    } catch {
      set({ activo: BRANDING_DEFAULT, activoAppId: null, activoAppEstado: null, interfazId: null })
    } finally {
      set({ cargando: false })
    }
  },

  async guardar(b) {
    const { interfazId } = get()
    const token = useAuthStore.getState().token
    if (!interfazId || !token) return false
    // EDITAR (publicada→generada con el branding nuevo) → PUBLICAR (versión nueva).
    const base = `/canonico/${rutaInterfaz()}/instancias/${interfazId}/transiciones`
    await api.post<any>(base, token, {
      verbo: 'EDITAR',
      payload: {
        logo_url: b.logo_url ?? null,
        nombre_comercial: b.nombre_comercial ?? null,
        color_primario: b.color_primario ?? null,
        color_acento: b.color_acento ?? null,
        favicon_url: b.favicon_url ?? null,
      },
    })
    await api.post<any>(base, token, { verbo: 'PUBLICAR', payload: {} })
    set({ activo: { ...get().activo, ...b } })
    return true
  },

  reset() {
    set({ activo: BRANDING_DEFAULT, activoAppId: null, activoAppEstado: null, interfazId: null })
  },
}))
